import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, RefreshCw } from 'lucide-react';

const PrivateRoute = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [tokenExpired, setTokenExpired] = useState(false);

    useEffect(() => {
        checkAuthStatus();
    }, []);

    const checkAuthStatus = async () => {
        try {
            const token = localStorage.getItem('authToken');

            if (!token) {
                setIsAuthenticated(false);
                setIsLoading(false);
                return;
            }

            // Verify token with backend
            const response = await fetch('https://api.zapbooksai.subraps.com/api/auth/verify', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (data.success) {
                setIsAuthenticated(true);
                setTokenExpired(false);
            } else {
                // Token is invalid or expired
                handleTokenError(data);
            }
        } catch (error) {
            console.error('Auth check error:', error);
            setIsAuthenticated(false);
            setTokenExpired(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTokenError = (data) => {
        if (data.code === 'TOKEN_EXPIRED') {
            setTokenExpired(true);
        }

        // Clear local storage
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userCompanies');

        setIsAuthenticated(false);
    };

    const handleRefreshToken = async () => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem('authToken');

            if (!token) {
                setIsAuthenticated(false);
                return;
            }

            const response = await fetch('https://api.zapbooksai.subraps.com/api/auth/refresh-token', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (data.success) {
                // Update stored token and user data
                localStorage.setItem('authToken', data.data.token);
                localStorage.setItem('userData', JSON.stringify(data.data.user));
                localStorage.setItem('userCompanies', JSON.stringify(data.data.companies));

                setIsAuthenticated(true);
                setTokenExpired(false);
            } else {
                handleTokenError(data);
            }
        } catch (error) {
            console.error('Token refresh error:', error);
            setIsAuthenticated(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userCompanies');
        setIsAuthenticated(false);
        setTokenExpired(false);

        // Force page reload to reset app state
        window.location.reload();
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-lg mb-4">
                        <RefreshCw className="w-8 h-8 text-teal-600 animate-spin" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Verifying Authentication</h2>
                    <p className="text-gray-600">Please wait while we verify your credentials...</p>
                </div>
            </div>
        );
    }

    // Token expired state
    if (tokenExpired) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
                        <AlertTriangle className="w-8 h-8 text-yellow-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Session Expired</h2>
                    <p className="text-gray-600 mb-6">Your session has expired. Please refresh your session or login again.</p>

                    <div className="space-y-3">
                        <button
                            onClick={handleRefreshToken}
                            disabled={isLoading}
                            className="w-full bg-teal-600 text-white py-2.5 px-4 rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                        >
                            {isLoading ? (
                                <div className="flex items-center justify-center gap-2">
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                    Refreshing...
                                </div>
                            ) : (
                                <div className="flex items-center justify-center gap-2">
                                    <RefreshCw className="w-4 h-4" />
                                    Refresh Session
                                </div>
                            )}
                        </button>

                        <button
                            onClick={handleLogout}
                            className="w-full bg-gray-200 text-gray-800 py-2.5 px-4 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
                        >
                            Login Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Not authenticated state
    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                        <Shield className="w-8 h-8 text-red-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
                    <p className="text-gray-600 mb-6">You need to be logged in to access this page.</p>

                    <button
                        onClick={handleLogout}
                        className="w-full bg-teal-600 text-white py-2.5 px-4 rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-colors font-medium"
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    // Authenticated - render children
    return <>{children}</>;
};

export default PrivateRoute;